import React from "react";
import { Box, TextField, MenuItem, Paper, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  inputsContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  inputField: {
    width: "100%",
  },
}));

const ReportInputs = ({
  filter,
  params,
  properties,
  propertyManagers = [],
  paymentPlanStatus,
  customerStatus,
  disconnectStatus,
  handleChange,
}) => {
  const classes = useStyles();

  // Group inputs by their type/purpose
  const inputGroups = {
    "Date Range": [
      filter.startDate >= 1 && {
        id: "startDate",
        name: "startDate",
        type: "date",
        label: "Start Date",
        value: params.startDate,
      },
      filter.endDate >= 1 && {
        id: "endDate",
        name: "endDate",
        type: "date",
        label: "End Date",
        value: params.endDate,
      },
    ].filter(Boolean),

    Property: [
      filter.property >= 1 && {
        id: "property",
        name: "property",
        type: "select",
        label: "Property",
        value: params.property,
        options: [
          { uuid: "all", name: "All" },
          ...[...properties]
            .sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }))
            .map((prop) => ({ uuid: prop.uuid, name: prop.name })),
        ],
      },
      filter.propertyManager >= 1 && {
        id: "propertyManager",
        name: "propertyManager",
        type: "select",
        label: "Property Manager",
        value: params.propertyManager,
        options: [
          { uuid: "", name: "All", value: "" },
          ...[...propertyManagers]
            .sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }))
            .map((pm) => ({ uuid: pm.uuid, name: pm.name, value: pm.uuid })),
        ],
      },
    ].filter(Boolean),

    Status: [
      filter.paymentPlan >= 1 &&
        paymentPlanStatus.length > 0 && {
          id: "paymentPlan",
          name: "paymentPlan",
          type: "select",
          label: "Payment Plan",
          value: params.paymentPlan,
          options: paymentPlanStatus.map((status) => ({
            uuid: status.uuid,
            name: status.description,
          })),
        },
      filter.customerStatus >= 1 &&
        customerStatus.length > 0 && {
          id: "customerStatus",
          name: "customerStatus",
          type: "select",
          label: "Customer Status",
          value: params.customerStatus,
          options: [
            { uuid: "all", name: "All" },
            ...customerStatus.map((status) => ({
              uuid: status.uuid,
              name: status.description,
            })),
          ],
        },
      filter.disconnectStatus >= 1 &&
        disconnectStatus.length > 0 && {
          id: "disconnectStatus",
          name: "disconnectStatus",
          type: "select",
          label: "Disconnect Status",
          value: params.disconnectStatus,
          options: [
            { uuid: "all", name: "All", value: "all" },
            ...disconnectStatus.map((status) => ({
              uuid: status.uuid,
              name: status.description,
              value: status.value,
            })),
          ],
        },
    ].filter(Boolean),
  };

  // Remove empty groups
  const activeGroups = Object.entries(inputGroups).filter(
    ([_, inputs]) => inputs.length > 0,
  );

  if (activeGroups.length === 0) {
    return null;
  }

  return (
    <Paper className={classes.root} elevation={0} variant="outlined">
      {activeGroups.map(([groupName, inputs]) => (
        <Box key={groupName} mb={3}>
          <Typography variant="subtitle1" color="textSecondary">
            {groupName}
          </Typography>
          <Box className={classes.inputsContainer}>
            {inputs.map((input) => (
              <TextField
                slotProps={{ inputLabel: { shrink: true } }}
                key={input.id}
                id={input.id}
                name={input.name}
                type={input.type}
                label={input.label}
                value={input.value || ""}
                onChange={handleChange}
                select={input.type === "select"}
                className={classes.inputField}
                size="small"
              >
                {input.type === "select" &&
                  input.options.map((option) => (
                    <MenuItem
                      key={option.uuid}
                      value={option.value || option.name}
                    >
                      {option.name}
                    </MenuItem>
                  ))}
              </TextField>
            ))}
          </Box>
        </Box>
      ))}
    </Paper>
  );
};

export default ReportInputs;
